import React, { createContext, useContext, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import { on, setVolume } from "@/lib/a11yBus";

const KEY = "jt_a11y";
const DEFAULTS = { textSize: 0, contrast: false, dyslexia: false, reduceMotion: false, links: false, captions: true, lyrics: false, notices: false, beatFlash: false, volume: 1, rate: 1 };
const A11yContext = createContext(null);

const load = () => {
  try {
    const s = { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
    if (localStorage.getItem(KEY) === null && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) s.reduceMotion = true;
    return s;
  } catch (e) { return DEFAULTS; }
};

export const speak = (text, rate = 1, { id = null, onEnd } = {}) => {
  if (!("speechSynthesis" in window)) return false;
  window.speechSynthesis.cancel();
  window.speechSynthesis.resume();
  window.dispatchEvent(new CustomEvent("jt-speak", { detail: id }));
  const u = new SpeechSynthesisUtterance(text);
  u.rate = rate;
  u.lang = "en-GB";
  if (onEnd) { u.onend = onEnd; u.onerror = onEnd; }
  window.speechSynthesis.speak(u);
  return true;
};
export const stopSpeaking = () => { window.speechSynthesis?.cancel(); window.dispatchEvent(new CustomEvent("jt-speak", { detail: null })); };

const SoundCaption = () => {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let t;
    const off = on("notice", (m) => { setMsg(m); clearTimeout(t); t = setTimeout(() => setMsg(null), 2600); });
    return () => { off(); clearTimeout(t); };
  }, []);
  if (!msg) return null;
  return (
    <div className="pointer-events-none fixed bottom-24 left-1/2 z-[120] -translate-x-1/2 rounded-xl bg-black/90 px-4 py-2 text-sm font-medium text-white shadow-xl" data-testid="sound-caption">
      [{msg}]
    </div>
  );
};

const BeatFlash = () => {
  const [beat, setBeat] = useState(null);
  useEffect(() => on("beat", (down) => { setBeat(down ? "down" : "beat"); setTimeout(() => setBeat(null), 120); }), []);
  return (
    <div aria-hidden="true" data-testid="beat-flash" className={`pointer-events-none fixed right-5 top-24 z-[120] h-10 w-10 rounded-full border-4 transition-colors duration-75 ${beat === "down" ? "border-red-500 bg-red-500" : beat ? "border-amber-400 bg-amber-400" : "border-amber-400/50 bg-transparent"}`} />
  );
};

export const A11yProvider = ({ children }) => {
  const [s, setS] = useState(load);
  const [live, setLive] = useState("");
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  useEffect(() => {
    const r = document.documentElement.classList;
    ["a11y-text-1", "a11y-text-2", "a11y-text-3"].forEach((c) => r.remove(c));
    if (s.textSize) r.add(`a11y-text-${s.textSize}`);
    r.toggle("a11y-contrast", s.contrast);
    r.toggle("a11y-dyslexia", s.dyslexia);
    r.toggle("a11y-reduce-motion", s.reduceMotion);
    r.toggle("a11y-links", s.links);
    setVolume(s.volume);
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* storage blocked */ }
  }, [s]);
  useEffect(() => on("notice", (m) => setLive(m)), []);
  return (
    <A11yContext.Provider value={{ ...s, set, reset: () => setS(DEFAULTS) }}>
      <MotionConfig reducedMotion={s.reduceMotion ? "always" : "user"}>{children}</MotionConfig>
      <div className="sr-only" aria-live="polite" data-testid="a11y-live">{live}</div>
      {s.notices && <SoundCaption />}
      {s.beatFlash && <BeatFlash />}
    </A11yContext.Provider>
  );
};

export const useA11y = () => useContext(A11yContext);
